<template>
  <PublicLayout>
    <main class="home">
      <!-- Hero Section -->
      <section class="hero" aria-label="Hero introduction">
        <div class="hero-content">
          <div class="hero-text">
            <h1>
              <span class="main-text">Work is great —<br/>when you<br/><span class="highlight">appreciate</span></span>
              <span class="playful">(Prec8!)</span>
            </h1>
            <p class="hero-subtitle">Transform your team culture with simple recognition that brings teams together</p>
            <div class="cta-buttons">
              <AddToSlackButton />
              <button @click="signIn" class="sign-in-button" aria-label="Sign in to your account">Sign In</button>
            </div>
          </div>
          <div class="hero-image" aria-hidden="true">
            <div class="floating-card card-1">
              <span class="emoji" role="img" aria-label="celebration">🎉</span>
              <p>Great job on the release! <span class="mention">@harry</span></p>
            </div>
            <div class="floating-card card-2">
              <span class="emoji" role="img" aria-label="star">⭐️</span>
              <p><span class="mention">@jean</span> thanks for helping out!</p>
            </div>
          </div>
        </div>
        <div class="hero-shape" aria-hidden="true"></div>
      </section>

      <!-- Features Section -->
      <section class="features" aria-label="Key features">
        <div class="section-content">
          <h2>Recognition Made <span class="highlight">Simple</span></h2>
          <div class="features-grid">
            <article class="feature-card">
              <div class="feature-icon" role="img" aria-label="target">🎯</div>
              <h3>Give Points</h3>
              <p>Recognize teammates with a simple Slack message and emoji reactions</p>
            </article>
            <article class="feature-card">
              <div class="feature-icon" role="img" aria-label="trophy">🏆</div>
              <h3>Track Progress</h3>
              <p>Watch your team's appreciation grow with real-time analytics and leaderboards</p>
            </article>
            <article class="feature-card">
              <div class="feature-icon" role="img" aria-label="flexed biceps">💪</div>
              <h3>Build Culture</h3>
              <p>Create a positive team environment that celebrates achievements and collaboration</p>
            </article>
          </div>
        </div>
        <div class="features-shape" aria-hidden="true"></div>
      </section>

      <!-- CTA Section -->
      <section class="cta-section" aria-label="Call to action">
        <div class="section-content">
          <h2>Ready to Transform Your <span class="highlight">Team Culture</span>?</h2>
          <p>Join thousands of teams using Prec8 to build stronger, more appreciative workplaces.</p>
          <div class="cta-buttons">
            <AddToSlackButton />
            <button @click="signIn" class="sign-in-button secondary">Sign In</button>
          </div>
        </div>
      </section>
    </main>
  </PublicLayout>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import PublicLayout from './components/PublicLayout.vue'
import AddToSlackButton from './components/AddToSlackButton.vue'
import { useAuthStore } from './stores/authStore'

@Options({
  components: {
    PublicLayout,
    AddToSlackButton
  },
})
export default class Home extends Vue {
  figletText = "";

  async mounted() {
    // Check if user is already authenticated and redirect to dashboard
    try {
      const { checkAuth } = useAuthStore();
      const authData = await checkAuth();
      
      if (authData.isAuthenticated) {
        this.$router.push('/dashboard');
      }
    } catch (error) {
      console.log('User not authenticated, staying on home page');
    }
  }

  signIn() {
    this.$router.push("/sign-in");
  }
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;600&display=swap');

.home {
  color: #1a1a1a;
  line-height: 1.5;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
}

.hero {
  position: relative;
  padding: 4rem 2rem;
  background: linear-gradient(135deg, #f6f9fc 0%, #eef2f7 100%);
  overflow: hidden;
  width: 100%;

  .hero-content {
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
    position: relative;
    z-index: 2;
  }

  .hero-text {
    h1 {
      font-size: 4rem;
      font-weight: 800;
      line-height: 1.1;
      margin-bottom: 1.5rem;

      .main-text {
        display: block;
        margin-bottom: 0.5rem;
      }

      .highlight {
        color: #FFD700;
      }

      .playful {
        font-family: 'Caveat', cursive;
        font-size: 2.5rem;
        color: #FF6B6B;
        display: block;
        margin-top: 1rem;
        text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
      }
    }

    .hero-subtitle {
      font-size: 1.5rem;
      margin-bottom: 2.5rem;
      opacity: 0.9;
      line-height: 1.6;
    }

    .cta-buttons {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;
    }

    .sign-in-button {
      padding: 1rem 2rem;
      background: white;
      color: #1a1a1a;
      border: 2px solid #e1e5e9;
      border-radius: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #f6f9fc;
        color: #cbd5e0;
      }
    }
  }

  .hero-image {
    position: relative;
    height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;

    .floating-card {
      position: absolute;
      background: white;
      color: #333;
      padding: 1.5rem;
      border-radius: 16px;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
      max-width: 280px;
      animation: float 6s ease-in-out infinite;

      .emoji {
        font-size: 2rem;
        display: block;
        margin-bottom: 0.5rem;
      }

      .mention {
        color: #667eea;
        font-weight: 600;
      }

      &.card-1 {
        top: 20%;
        left: 10%;
        animation-delay: 0s;
      }

      &.card-2 {
        bottom: 20%;
        right: 10%;
        animation-delay: 3s;
      }
    }
  }

  .hero-shape {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: white;
    clip-path: polygon(0 100%, 100% 100%, 100% 0, 0 70%);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

.features {
  position: relative;
  padding: 6rem 2rem;
  background: white;
  width: 100%;

  .section-content {
    max-width: 1200px;
    margin: 0 auto;
    text-align: center;
  }

  h2 {
    font-size: 3rem;
    font-weight: 800;
    margin-bottom: 3rem;
    color: #1a1a1a;

    .highlight {
      color: #667eea;
    }
  }

  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-top: 3rem;
  }

  .feature-card {
    background: #f8fafc;
    padding: 3rem 2rem;
    border-radius: 20px;
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-5px);
    }

    .feature-icon {
      font-size: 3rem;
      margin-bottom: 1.5rem;
    }

    h3 {
      font-size: 1.8rem;
      font-weight: 700;
      margin-bottom: 1rem;
      color: #1a1a1a;
    }

    p {
      color: #666;
      line-height: 1.6;
    }
  }

  .features-shape {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: #f8fafc;
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 30%);
  }
}

.benefits {
  position: relative;
  padding: 6rem 2rem;
  background: #f6f9fc;
  width: 100%;

  .section-content {
    max-width: 1200px;
    margin: 0 auto;
    text-align: center;
  }

  h2 {
    font-size: 3rem;
    font-weight: 800;
    margin-bottom: 3rem;
    color: #1a1a1a;
  }

  .benefits-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 2rem;
    margin-top: 3rem;
  }

  .benefit-item {
    background: white;
    padding: 2.5rem;
    border-radius: 20px;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-5px);
    }

    h3 {
      font-size: 1.8rem;
      font-weight: 700;
      margin-bottom: 1rem;
      color: #1a1a1a;
    }

    p {
      color: #666;
      line-height: 1.6;
    }
  }
}

.cta-section {
  position: relative;
  padding: 6rem 2rem;
  background: #f6f9fc;
  width: 100%;

  .section-content {
    max-width: 1200px;
    margin: 0 auto;
    text-align: center;
  }

  h2 {
    font-size: 3rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    color: #1a1a1a;
  }

  p {
    font-size: 1.25rem;
    color: #666;
    margin-bottom: 2.5rem;
  }

  .cta-buttons {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  .sign-in-button.secondary {
    padding: 1rem 2rem;
    background: #FF6B6B;
    color: white;
    border: none;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #FF8E53;
    }
  }
}

@media (max-width: 768px) {
  .hero {
    padding: 2rem 1rem;

    .hero-content {
      grid-template-columns: 1fr;
      gap: 2rem;
      text-align: center;
    }

    .hero-text h1 {
      font-size: 2.5rem;
    }

    .hero-subtitle {
      font-size: 1.25rem;
    }

    .cta-buttons {
      justify-content: center;
    }

    .hero-image {
      height: 300px;
    }

    .floating-card {
      max-width: 240px;
      padding: 1rem;
      font-size: 0.9rem;

      .emoji {
        font-size: 1.5rem;
      }
    }
  }

  .features {
    padding: 3rem 1rem;

    h2 {
      font-size: 2rem;
    }

    .features-grid {
      grid-template-columns: 1fr;
    }
  }

  .benefits {
    padding: 3rem 1rem;

    h2 {
      font-size: 2rem;
    }

    .benefits-grid {
      grid-template-columns: 1fr;
    }
  }

  .cta-section {
    padding: 3rem 1rem;

    h2 {
      font-size: 2rem;
    }

    p {
      font-size: 1rem;
    }

    .cta-buttons {
      flex-direction: column;
    }
  }
}
</style>
